'use client';

import { useEffect } from 'react';
import { ZERO } from '@velocity-exchange/sdk';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { notify } from 'src/utils/notifications';
import { useImmer } from 'use-immer';
import { useAccountsBeingLiquidatedWithoutBankruptcies } from './useAccountsBeingLiquidated';
import usePositionsBeingLiquidated from './usePositionsBeingLiquidated';
import useModal from 'src/components/Modals/hooks/useModal';
import Link from 'src/components/primitives/Link/Link';
import Button from 'src/components/primitives/Button/Button';
import { UIMarket } from '@velocity-exchange/common';

// Limit alerts per user to once every 30 seconds
const ALERT_RATE_LIMIT_MS = 30 * 1000;

const useLiquidationAlerts = () => {
	const { show } = useModal();
	const userAccounts = useLazySubAccounts();
	const accountsBeingLiquidated =
		useAccountsBeingLiquidatedWithoutBankruptcies();
	const positionsBeingLiquidated = usePositionsBeingLiquidated();
	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const accountsMap = useDriftAccountStore((s) => s.accounts);

	const hasMultipleAccounts = userAccounts.length > 0;

	const [lastShownLiqAlertTs, setLastShownLiqAlertTs] = useImmer<
		Record<string, number>
	>({});

	useEffect(() => {
		const now = Date.now();

		// Look for new alerts which need to be displayed
		const liqAlertsToAdd = accountsBeingLiquidated.filter(
			(account) =>
				!lastShownLiqAlertTs[account.userKey] ||
				lastShownLiqAlertTs[account.userKey] + ALERT_RATE_LIMIT_MS < now
		);

		liqAlertsToAdd.forEach((alertToAdd) => {
			const alertId = `alertOpen_${alertToAdd.userKey}`;

			// Skip adding this alert if it breaks the rate limit
			const shouldSkipAlert =
				lastShownLiqAlertTs[alertToAdd.userKey] &&
				lastShownLiqAlertTs[alertToAdd.userKey] + ALERT_RATE_LIMIT_MS > now;

			if (shouldSkipAlert) return;

			// Otherwise we're good to add it, with a 5 second timer before it disappears
			setLastShownLiqAlertTs((s) => {
				s[alertToAdd.userKey] = now;
			});

			const isCurrentAccount = alertToAdd.userKey === currentUserKey;
			const accountName = accountsMap[alertToAdd.userKey]?.name ?? 'Account';

			const description = isCurrentAccount
				? 'Your account is being liquidated'
				: `Your account (${accountName}) is being liquidated`;

			notify({
				type: 'awaiting',
				id: alertId,
				message: 'LIQUIDATION IN PROGRESS',
				description,
				showUntilCancelled: true,
				updatePrevious: true,
				action: {
					type: 'custom',
					content: (
						<div className="flex items-center gap-x-sm">
							<Button
								size="sm"
								variant="stroke"
								onClick={() => {
									show({
										type: 'deposit',
										props: {
											targetAccountKey: alertToAdd.userKey,
											defaultLargestBalance: true,
										},
									});
								}}
							>
								Deposit
							</Button>
							<Link
								href="https://docs.drift.trade/liquidations/liquidations"
								openInNewTab
							>
								Learn More
							</Link>
						</div>
					),
				},
			});
		});

		// Isolated position liquidation alerts (per position)
		positionsBeingLiquidated.forEach((item) => {
			const marketIndex = item.position.marketIndex;
			const key = `${item.userKey}_${marketIndex}`;

			const shouldSkipAlert =
				lastShownLiqAlertTs[key] &&
				lastShownLiqAlertTs[key] + ALERT_RATE_LIMIT_MS > now;

			if (shouldSkipAlert) return;

			setLastShownLiqAlertTs((s) => {
				s[key] = now;
			});

			const uiMarket = UIMarket.createPerpMarket(marketIndex);
			const marketLabel = uiMarket.uiSymbols.marketDisplaySymbol ?? '';

			const isCurrentAccount = item.userKey === currentUserKey;
			const accountName = accountsMap[item.userKey]?.name ?? 'Account';

			// Check if position is fully liquidated (zero size)
			const isFullyLiquidated = item.position.baseAssetAmount.eq(ZERO);

			let description: string;
			if (isFullyLiquidated) {
				// Past tense - liquidation complete
				description = isCurrentAccount
					? `${marketLabel} (Isolated) is liquidated`
					: `Your account (${accountName})'s ${marketLabel} (Isolated) is liquidated`;
			} else {
				// Present tense - liquidation in progress
				description = isCurrentAccount
					? `${marketLabel} (Isolated) is being liquidated`
					: `Your account (${accountName})'s ${marketLabel} (Isolated) is being liquidated`;
			}

			const alertId = isFullyLiquidated
				? `alertComplete_${item.userKey}_${marketIndex}`
				: `alertOpen_${item.userKey}_${marketIndex}`;

			notify({
				type: isFullyLiquidated ? 'alert' : 'awaiting',
				id: alertId,
				message: isFullyLiquidated
					? 'LIQUIDATION COMPLETE'
					: 'LIQUIDATION IN PROGRESS',
				description,
				showUntilCancelled: !isFullyLiquidated,
				updatePrevious: true,
			});
		});

		// Handle hiding alerts for these accounts
	}, [
		accountsBeingLiquidated,
		positionsBeingLiquidated,
		hasMultipleAccounts,
		show,
		currentUserKey,
		accountsMap,
	]);
};

export default useLiquidationAlerts;
